import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, takeUntil, withLatestFrom } from 'rxjs/operators';
import { updateAstoriaOption } from '../../../store/actions/functional-elements.actions';
import { FormsState } from '../../../store/state/pace-forms.state';
import { AstoriaOption, OPTION_FUNCTIONAL_ELEMENT_TYPE } from '../../../types/option';
import { BaseOptionComponent } from '../base-option.component';

@Component({
    selector: 'pace-forms-astoria-option',
    templateUrl: './astoria-option.component.html',
    styleUrls: ['./astoria-option.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AstoriaOptionComponent extends BaseOptionComponent implements OnInit {
    // make enum accessible in HTML
    public OPTION_FUNCTIONAL_ELEMENT_TYPE = OPTION_FUNCTIONAL_ELEMENT_TYPE;
    declare public optionFunctionalElement$: Observable<AstoriaOption>;
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        protected readonly store: Store<FormsState>,
        protected readonly formBuilder: FormBuilder,
    ) {
        super(log, store, formBuilder);
    }

    public ngOnInit(): void {
        super.ngOnInit();

        this.radioInputControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                withLatestFrom(this.optionFunctionalElement$),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([value, option]: [OPTION_FUNCTIONAL_ELEMENT_TYPE | null, AstoriaOption]) => {
                const selected = value === null ? undefined : value;

                // this needs to be taken out of the state because we might have switched participant copies and then
                // the previousSelection store in the component might not correctly reflect the actual previous
                // selection for that participant copy
                // if there was no selection, then we still want to dispatch the action
                if (selected === option.selected && isDefined(option.selected)) {
                    return;
                }

                this.store.dispatch(
                    updateAstoriaOption({
                        id: option.id,
                        xmlId: option.xmlId,
                        selected: selected,
                    }),
                );
            });

        combineLatest([this.optionFunctionalElement$, this.readOnly$])
            .pipe(takeUntil(this.destroyTrigger$))
            .subscribe(([option, readOnly]) => {
                if (typeof option.selected === 'undefined') {
                    this.radioInputControl.reset();
                } else {
                    this.radioInputControl.patchValue(option.selected);
                }
                this.radioInputControl[option.disabled || readOnly ? 'disable' : 'enable']({
                    emitEvent: false,
                });
            });
    }
}
